import { supabase } from '../lib/supabase';

/**
 * Service for handling resume scan comparisons and tracking improvements
 */
export class ComparisonService {
    /**
     * Finds the most relevant previous scan to compare against
     * Looks for the most recent scan with the same target role
     * @param {string} userId - User's ID
     * @param {string} targetRole - The job role being analyzed
     * @param {string} excludeId - Current scan ID to exclude (optional)
     * @returns {Promise<Object|null>} Previous scan or null
     */
    static async findComparisonScan(userId, targetRole, excludeId = null, jobUrl = null) {
        try {
            if (!userId || !targetRole) {
                console.log('Missing userId or targetRole for comparison');
                return null;
            }

            let query = supabase
                .from('resume_analyses')
                .select('*')
                .eq('user_id', userId)
                .eq('status', 'completed')
                .order('created_at', { ascending: false })
                .limit(20);

            if (excludeId) {
                query = query.neq('id', excludeId);
            }

            const { data, error } = await query;

            if (error || !data || data.length === 0) {
                console.log('No previous scans found for comparison');
                return null;
            }

            console.log(`🔍 Found ${data.length} previous scans to check`);

            // PRIORITY 1: Try to find a scan for the same job URL (most accurate)
            if (jobUrl) {
                const sameJobScans = data.filter(scan => {
                    const scanJobUrl = scan.analysis_results?.jobPosting?.job_url;
                    if (!scanJobUrl) return false;

                    // Normalize URLs for comparison
                    const normalizeUrl = (url) => {
                        try {
                            const urlObj = new URL(url);
                            return `${urlObj.origin}${urlObj.pathname}`.toLowerCase().replace(/\/$/, '');
                        } catch {
                            return url.toLowerCase().trim();
                        }
                    };

                    return normalizeUrl(scanJobUrl) === normalizeUrl(jobUrl);
                });

                if (sameJobScans.length > 0) {
                    console.log(`✅ Found ${sameJobScans.length} scans for same job URL`);
                    return sameJobScans[0];
                }
            }

            // PRIORITY 2: Fall back to role name matching (for backward compatibility)


            // Try to find a scan for the same role
            const sameRoleScans = data.filter(scan => {
                // Check multiple possible locations for target role
                const scanRole = scan.target_role ||
                    scan.analysis_results?.targetRole ||
                    scan.analysis_results?.analysis?.job_role ||
                    scan.analysis_results?.jobPosting?.title;

                if (!scanRole) return false;

                const normalized1 = scanRole.toLowerCase().trim();
                const normalized2 = targetRole.toLowerCase().trim();

                return normalized1 === normalized2 ||
                    normalized1.includes(normalized2) ||
                    normalized2.includes(normalized1);
            });

            if (sameRoleScans.length > 0) {
                console.log(`✅ Found ${sameRoleScans.length} scans for same role: "${targetRole}" (fallback match)`);
                return sameRoleScans[0];
            }

            console.log(`No same-role scan found for "${targetRole}"`);
            return null;
        } catch (error) {
            console.error('Error finding comparison scan:', error);
            return null;
        }
    }

    /**
     * Compares current scan with a previous scan
     * @param {Object} currentScan - Current analysis data
     * @param {Object} previousScan - Previous analysis data
     * @returns {Object} Comparison results with deltas and insights
     */
    static calculateComparison(currentScan, previousScan) {
        try {
            // Extract analysis data from different possible structures
            // Extract analysis data from different possible structures
            const current = currentScan.analysis_results?.analysis ||
                currentScan.analysis_results ||
                currentScan.analysis || // Handle raw payload structure
                currentScan;

            const previous = previousScan.analysis_results?.analysis ||
                previousScan.analysis_results ||
                previousScan.analysis ||
                previousScan;

            // Calculate score improvements
            const currentOverall = current.overall_resume_score || currentScan.overall_score || 0;
            const previousOverall = previous.overall_resume_score || previousScan.overall_score || 0;
            const overallScoreDelta = currentOverall - previousOverall;

            const currentJobMatch = current.job_match_score || currentScan.job_match_score || 0;
            const previousJobMatch = previous.job_match_score || previousScan.job_match_score || 0;
            const jobMatchScoreDelta = currentJobMatch - previousJobMatch;

            // Extract skills from various possible locations
            const extractSkills = (data) => {
                const skills = data.skills_found ||
                    data.skills ||
                    data.extracted_skills ||
                    [];
                return skills.map(s => {
                    if (typeof s === 'string') return s.toLowerCase();
                    return (s.name || s.skill_name || '').toLowerCase();
                }).filter(Boolean);
            };

            const currentSkills = new Set(extractSkills(current));
            const previousSkills = new Set(extractSkills(previous));

            const newSkills = [...currentSkills].filter(s => !previousSkills.has(s));
            const lostSkills = [...previousSkills].filter(s => !currentSkills.has(s));
            const retainedSkills = [...currentSkills].filter(s => previousSkills.has(s));

            // Analyze missing skills gap closure
            const extractMissingSkills = (data) => {
                const missing = data.skills_missing || data.missing_skills || [];
                return missing.map(s => {
                    if (typeof s === 'string') return s.toLowerCase();
                    return (s.name || s.skill_name || '').toLowerCase();
                }).filter(Boolean);
            };

            const currentMissing = new Set(extractMissingSkills(current));
            const previousMissing = new Set(extractMissingSkills(previous));

            // Skills that were missing before but are now present
            const addressedSkills = [...previousMissing].filter(s => currentSkills.has(s));
            const newMissingSkills = [...currentMissing].filter(s => !previousMissing.has(s));

            // Calculate overall improvement score (-100 to +100)
            const improvementScore = Math.round(
                (overallScoreDelta * 0.4) +
                (jobMatchScoreDelta * 0.4) +
                (newSkills.length * 2) -
                (lostSkills.length * 2) +
                (addressedSkills.length * 3) -
                (newMissingSkills.length * 1)
            );

            const daysBetween = Math.floor(
                (new Date(currentScan.created_at) - new Date(previousScan.created_at)) /
                (1000 * 60 * 60 * 24)
            );

            console.log(`📊 Comparison calculated: ${improvementScore} improvement score`);

            return {
                scores: {
                    overall: {
                        previous: previousOverall,
                        current: currentOverall,
                        delta: overallScoreDelta,
                        percentChange: previousOverall > 0
                            ? Math.round((overallScoreDelta / previousOverall) * 100)
                            : 0
                    },
                    jobMatch: {
                        previous: previousJobMatch,
                        current: currentJobMatch,
                        delta: jobMatchScoreDelta,
                        percentChange: previousJobMatch > 0
                            ? Math.round((jobMatchScoreDelta / previousJobMatch) * 100)
                            : 0
                    }
                },
                skills: {
                    new: newSkills,
                    lost: lostSkills,
                    retained: retainedSkills.length,
                    totalCurrent: currentSkills.size,
                    totalPrevious: previousSkills.size
                },
                skillGapClosure: {
                    addressed: addressedSkills, // Skills that were missing, now present
                    newGaps: newMissingSkills,  // New skills now missing
                    totalAddressed: addressedSkills.length,
                    remainingGaps: currentMissing.size
                },
                improvementScore,
                improvementLevel: this.getImprovementLevel(improvementScore),
                scanDates: {
                    previous: previousScan.created_at,
                    current: currentScan.created_at,
                    daysBetween
                }
            };
        } catch (error) {
            console.error('Error calculating comparison:', error);
            return null;
        }
    }

    /**
     * Get improvement level description based on score
     * @param {number} score - Improvement score (-100 to +100)
     * @returns {Object} Level info with label and color
     */
    static getImprovementLevel(score) {
        if (score >= 20) return { level: 'excellent', label: 'Excellent Progress 🎉', color: 'green', emoji: '🎉' };
        if (score >= 10) return { level: 'good', label: 'Good Progress 👍', color: 'blue', emoji: '👍' };
        if (score >= 5) return { level: 'slight', label: 'Making Progress 📈', color: 'cyan', emoji: '📈' };
        if (score >= -5) return { level: 'minimal', label: 'Minimal Change', color: 'gray', emoji: '➖' };
        return { level: 'declined', label: 'Needs Attention ⚠️', color: 'red', emoji: '⚠️' };
    }

    /**
     * Stores comparison data when creating a new scan
     * Automatically finds and compares with previous scan for same role
     * @param {string} userId - User's ID
     * @param {string} currentScanId - Current scan ID
     * @param {string} targetRole - Target job role
     * @param {Object} currentScan - Current scan data (optional, will fetch if not provided)
     * @returns {Promise<Object|null>} Comparison data or null
     */
    static async storeComparisonIfAvailable(userId, currentScanId, targetRole, currentScan = null, explicitPreviousScanId = null) {
        try {
            console.log(`🔍 Looking for previous scan to compare (role: "${targetRole}")`);

            let previousScan = null;

            // ONLY do comparison if user explicitly requested it via re-scan button
            if (explicitPreviousScanId) {
                console.log(`🎯 User explicitly selected previous scan: ${explicitPreviousScanId}`);
                const { data, error } = await supabase
                    .from('resume_analyses')
                    .select('*')
                    .eq('id', explicitPreviousScanId)
                    .single();

                if (!error && data) {
                    previousScan = data;
                    console.log(`✅ Using user-selected previous scan from ${new Date(data.created_at).toLocaleDateString()}`);
                } else {
                    console.log('⚠️ Could not find explicitly selected scan');
                }
            } else {
                console.log('✨ Fresh scan - no comparison performed');
            }



            if (!previousScan) {
                console.log('✨ This is the first scan for this role - no comparison available');
                // Still store the target_role for future comparisons
                await supabase
                    .from('resume_analyses')
                    .update({ target_role: targetRole })
                    .eq('id', currentScanId);
                return null;
            }

            console.log(`📊 Found previous scan from ${new Date(previousScan.created_at).toLocaleDateString()}`);

            // Get current scan data if not provided
            if (!currentScan) {
                const { data, error } = await supabase
                    .from('resume_analyses')
                    .select('*')
                    .eq('id', currentScanId)
                    .single();

                if (error || !data) {
                    console.error('Failed to retrieve current scan for comparison');
                    return null;
                }
                currentScan = data;
            }

            // Calculate comparison
            const comparison = this.calculateComparison(currentScan, previousScan);

            if (!comparison) {
                console.error('Failed to calculate comparison');
                return null;
            }

            // Store comparison data in database
            const { error: updateError } = await supabase
                .from('resume_analyses')
                .update({
                    previous_scan_id: previousScan.id,
                    comparison_data: comparison,
                    improvement_score: comparison.improvementScore,
                    target_role: targetRole // Store for easier querying
                })
                .eq('id', currentScanId);

            if (updateError) {
                console.error('Failed to store comparison:', updateError);
                return null;
            }

            console.log(`✅ Comparison stored: ${comparison.improvementScore} improvement score (${comparison.improvementLevel.label})`);
            return comparison;
        } catch (error) {
            console.error('Error storing comparison:', error);
            return null;
        }
    }

    /**
     * Retrieves comparison data for a scan
     * @param {string} scanId - Scan ID
     * @returns {Promise<Object|null>} Comparison data or null
     */
    static async getComparisonForScan(scanId) {
        try {
            const { data, error } = await supabase
                .from('resume_analyses')
                .select('comparison_data, previous_scan_id, improvement_score, target_role')
                .eq('id', scanId)
                .single();

            if (error || !data || !data.comparison_data) {
                return null;
            }

            return {
                ...data.comparison_data,
                previousScanId: data.previous_scan_id,
                targetRole: data.target_role
            };
        } catch (error) {
            console.error('Error getting comparison:', error);
            return null;
        }
    }

    /**
     * Gets all scans for a specific job role (for showing history)
     * @param {string} userId - User's ID
     * @param {string} targetRole - Target job role
     * @param {number} limit - Maximum number of scans to return
     * @returns {Promise<Array>} List of scans for the role
     */
    static async getScansForRole(userId, targetRole, limit = 10) {
        try {
            const { data, error } = await supabase
                .from('resume_analyses')
                .select('id, created_at, overall_score, job_match_score, improvement_score, status')
                .eq('user_id', userId)
                .eq('target_role', targetRole)
                .eq('status', 'completed')
                .order('created_at', { ascending: false })
                .limit(limit);

            if (error) throw error;
            return data || [];
        } catch (error) {
            console.error('Error getting scans for role:', error);
            return [];
        }
    }

    /**
     * Generates enhanced LLM-powered comparison analysis
     * @param {string} userId - User's ID
     * @param {string} currentScanId - Current scan ID
     * @param {string} previousScanId - Previous scan ID (optional, will be found if not provided)
     * @returns {Promise<Object|null>} Enhanced comparison data from LLM
     */
    static async generateEnhancedComparison(userId, currentScanId, previousScanId = null) {
        try {
            console.log('🤖 Generating enhanced LLM comparison...');

            // Get current scan
            const { data: currentScan, error: currentError } = await supabase
                .from('resume_analyses')
                .select('*')
                .eq('id', currentScanId)
                .single();

            if (currentError || !currentScan) {
                console.error('Failed to fetch current scan');
                return null;
            }

            // Get previous scan (find if not provided)
            let previousScan;
            if (previousScanId) {
                const { data, error } = await supabase
                    .from('resume_analyses')
                    .select('*')
                    .eq('id', previousScanId)
                    .single();

                if (error || !data) {
                    console.error('Failed to fetch previous scan');
                    return null;
                }
                previousScan = data;
            } else {
                // Fresh scan - don't search for comparisons automatically
                console.log('✨ Fresh scan - skipping enhanced comparison');
                return null;
            }

            if (!previousScan) {
                console.log('No previous scan found for enhanced comparison');
                return null;
            }

            // Extract job details
            const jobDetails = {
                title: currentScan.target_role ||
                    currentScan.analysis_results?.jobPosting?.title ||
                    'Unknown',
                company: currentScan.analysis_results?.jobPosting?.company || 'Unknown',
                description: currentScan.analysis_results?.jobPosting?.description || ''
            };

            // Prepare data for Edge Function
            const payload = {
                previousScan: {
                    date: new Date(previousScan.created_at).toLocaleDateString(),
                    overall_score: previousScan.overall_score || 0,
                    job_match_score: previousScan.job_match_score || 0,
                    skills_found: this.extractSkillsList(previousScan),
                    skills_missing: this.extractMissingSkillsList(previousScan)
                },
                currentScan: {
                    date: new Date(currentScan.created_at).toLocaleDateString(),
                    overall_score: currentScan.overall_score || 0,
                    job_match_score: currentScan.job_match_score || 0,
                    skills_found: this.extractSkillsList(currentScan),
                    skills_missing: this.extractMissingSkillsList(currentScan)
                },
                jobDetails
            };

            // Get user tier from profile
            let userTier = 'premium'; // Default to premium for re-scans
            try {
                const { data: profile } = await supabase
                    .from('user_profiles')
                    .select('tier')
                    .eq('id', userId)
                    .single();

                if (profile?.tier) {
                    userTier = profile.tier;
                }
            } catch (error) {
                console.log('Could not fetch user tier, using default:', error);
            }

            // Add tier and userId to payload
            payload.tier = userTier;
            payload.userId = userId;

            // Call Edge Function
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
            const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
            const { data: sessionData } = await supabase.auth.getSession();
            const accessToken = sessionData?.session?.access_token;

            const response = await fetch(`${supabaseUrl}/functions/v1/comparison-analysis`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'apikey': supabaseAnonKey,
                    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const error = await response.text();
                console.error('Edge Function error:', error);
                return null;
            }

            const result = await response.json();

            if (!result.success || !result.comparison) {
                console.error('Invalid response from Edge Function');
                return null;
            }

            console.log('✅ Enhanced comparison generated successfully');
            return result.comparison;

        } catch (error) {
            console.error('Error generating enhanced comparison:', error);
            return null;
        }
    }

    /**
     * Helper: Extract skills list from scan
     */
    static extractSkillsList(scan) {
        const analysis = scan.analysis_results?.analysis || scan.analysis_results || {};
        const skills = analysis.skills_found || [];

        return skills.slice(0, 20).map(skill => {
            if (typeof skill === 'string') return skill;
            return skill.name || skill.skill_name || '';
        }).filter(Boolean);
    }

    /**
     * Helper: Extract missing skills list from scan
     */
    static extractMissingSkillsList(scan) {
        const analysis = scan.analysis_results?.analysis || scan.analysis_results || {};
        const missing = analysis.skills_missing || [];

        return missing.slice(0, 15).map(gap => ({
            name: gap.name || '',
            severity: gap.severity || 'recommended',
            reason: gap.reason || ''
        })).filter(item => item.name);
    }
}

export default ComparisonService;
